import { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Switch,
  Textarea,
  Select,
  SelectItem,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import { Prompt } from "@webuild-ai/admin/models/components";
import { useUser } from "@webuild-ai/react-auth";

import { availableLanguages } from "@/i18n";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { TagSelector } from "@/components/TagSelector/TagSelector";
import { blurActiveElement } from "@/utils/dom";
import { logger } from "@/telemetry";

interface SavePromptModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  promptToSave: string;
  initialData?: {
    title: string;
    description: string;
    tags: string[];
    public: boolean;
  };
  editingPrompt?: Prompt;
  onSuccess?: () => void;
}

export function SavePromptModal({
  isOpen,
  onOpenChange,
  promptToSave,
  initialData,
  editingPrompt,
  onSuccess,
}: SavePromptModalProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState(promptToSave);
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
    content?: string;
    sourceLanguage?: string;
  }>({});
  const { t, i18n } = useTranslation();
  const { adminClient } = useWebuildAPIClient();
  const { userProfile } = useUser();

  // Initialize source language with current app language
  useEffect(() => {
    if (!sourceLanguage) {
      setSourceLanguage(i18n.language);
    }
  }, [i18n.language, sourceLanguage]);

  const resetState = () => {
    setTitle("");
    setDescription("");
    setTags([]);
    setIsPublic(false);
    setSourceLanguage(i18n.language);
    setErrors({});
  };

  useEffect(() => {
    setContent(promptToSave);
  }, [promptToSave]);

  useEffect(() => {
    if (editingPrompt) {
      setTitle(editingPrompt.name);
      setDescription(editingPrompt.description);
      setTags(editingPrompt.tags || []);
      setIsPublic(editingPrompt.isPublic || false);
      setSourceLanguage(editingPrompt.sourceLanguage || i18n.language);
    } else if (initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description);
      setTags(initialData.tags);
      setIsPublic(initialData.public);
      setSourceLanguage(i18n.language);
    } else {
      resetState();
    }
  }, [editingPrompt, initialData, i18n.language]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  // Handle modal close with focus cleanup
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      blurActiveElement();
    }
    onOpenChange(open);
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!title.trim()) {
      newErrors.title = t("Title is required");
    }

    if (!description.trim()) {
      newErrors.description = t("Description is required");
    }

    if (!content.trim()) {
      newErrors.content = t("Content is required");
    }

    if (!sourceLanguage) {
      newErrors.sourceLanguage = t("Source language is required");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (field: keyof typeof errors) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        delete newErrors[field];

        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!adminClient || !userProfile?.tenant) {
      logger.error("Admin client or user profile not found");

      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      if (editingPrompt) {
        await adminClient.prompts.update({
          id: editingPrompt.id,
          prompt: {
            id: editingPrompt.id,
            tenant: userProfile.tenant,
            username: userProfile.id,
            name: title,
            description: description,
            content: content,
            tags: tags,
            isPublic: isPublic,
            sourceLanguage: sourceLanguage,
          },
        });
      } else {
        await adminClient.prompts.createPrompt({
          id: crypto.randomUUID(),
          tenant: userProfile.tenant,
          username: userProfile.id,
          name: title,
          description: description,
          content: content,
          tags: tags,
          isPublic: isPublic,
          sourceLanguage: sourceLanguage,
        });
      }

      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error("Error saving prompt", error);
    }
  };

  return (
    <Modal
      classNames={{
        closeButton: "top-3 right-3 p-0 hover:bg-default-100",
      }}
      isOpen={isOpen}
      size="3xl"
      onOpenChange={handleOpenChange}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1 font-bold text-brand-primary dark:!text-white">
              {editingPrompt ? t("Edit Prompt") : t("Add New Prompt")}
            </ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <div>
                  <Input
                    isRequired
                    className="focus:outline-none"
                    classNames={{
                      label: "relative",
                      input: "focus:outline-none",
                      inputWrapper: errors.title ? "border-danger" : "",
                    }}
                    color={errors.title ? "danger" : "default"}
                    data-id="save-prompt-title"
                    errorMessage={errors.title}
                    isInvalid={!!errors.title}
                    label={t("Title")}
                    placeholder={t("Enter a title for your prompt")}
                    value={title}
                    onValueChange={(value) => {
                      setTitle(value);
                      clearFieldError("title");
                    }}
                  />
                </div>

                {/* <Select
                data-id="save-prompt-team"
                isRequired
                label={t("Team")}
                placeholder={t("Select a team")}
                selectedKeys={team ? [team] : []}
                onChange={(e) => setTeam(e.target.value)}
              >
                {teams.map((teamName) => (
                  <SelectItem key={teamName}>{teamName}</SelectItem>
                ))}
              </Select> */}

                <div>
                  <Input
                    isRequired
                    className="focus:outline-none"
                    classNames={{
                      label: "relative",
                      input: "focus:outline-none",
                      inputWrapper: errors.description ? "border-danger" : "",
                    }}
                    color={errors.description ? "danger" : "default"}
                    data-id="save-prompt-description"
                    errorMessage={errors.description}
                    isInvalid={!!errors.description}
                    label={t("Description")}
                    placeholder={t("Enter a description")}
                    value={description}
                    onValueChange={(value) => {
                      setDescription(value);
                      clearFieldError("description");
                    }}
                  />
                </div>

                <div>
                  <Select
                    isRequired
                    className="focus:outline-none"
                    classNames={{
                      label: "relative",
                      trigger: errors.sourceLanguage ? "border-danger" : "",
                    }}
                    color={errors.sourceLanguage ? "danger" : "default"}
                    data-id="save-prompt-language"
                    errorMessage={errors.sourceLanguage}
                    isInvalid={!!errors.sourceLanguage}
                    label={t("Source Language")}
                    placeholder={t("Select source language")}
                    selectedKeys={sourceLanguage ? [sourceLanguage] : []}
                    onChange={(e) => {
                      setSourceLanguage(e.target.value);
                      clearFieldError("sourceLanguage");
                    }}
                  >
                    {availableLanguages.map((lang) => (
                      <SelectItem key={lang.code}>{lang.nativeName}</SelectItem>
                    ))}
                  </Select>
                </div>

                <div>
                  <Textarea
                    isRequired
                    className="focus:outline-none"
                    classNames={{
                      label: "relative",
                      input: "focus:outline-none",
                      inputWrapper: errors.content ? "border-danger" : "",
                    }}
                    color={errors.content ? "danger" : "default"}
                    data-id="save-prompt-content"
                    errorMessage={errors.content}
                    isInvalid={!!errors.content}
                    label={t("Content")}
                    minRows={5}
                    placeholder={t("Prompt content")}
                    value={content}
                    onValueChange={(value) => {
                      setContent(value);
                      clearFieldError("content");
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <TagSelector
                    label={t("Tags")}
                    selectedTags={tags}
                    showAddNewTag={true}
                    onTagsChange={setTags}
                  />
                  <div className="flex items-center space-x-2">
                    <Switch
                      data-id="save-prompt-public"
                      isSelected={isPublic}
                      onValueChange={setIsPublic}
                    />
                    <span className="text-xs text-gray-500">
                      {isPublic ? t("Shared with company") : t("Only you can access")}
                    </span>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button
                className="btn-brand-primary text-sm"
                data-id="save-prompt-cancel"
                onPress={onClose}
              >
                {t("Cancel")}
              </Button>
              <Button
                className="btn-brand-primary text-sm"
                data-id="save-prompt-save-button"
                onPress={handleSave}
              >
                {t("Save")}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
